<script setup>
import { computed, onMounted, ref } from "vue";
import { useDemoRequestStore } from "@/stores/demoRequestStore";

const title = "Demo Requests";
const text = "Superadmin";
const text1 = "Demo Requests";

const store = useDemoRequestStore();
const selectedRequest = ref(null);
const adminNotes = ref("");

const statusLabel = computed(() => {
  const value = store.filters.status;
  if (!value) return "All Statuses";
  return value.charAt(0).toUpperCase() + value.slice(1);
});

const statusBadgeClass = (status) => {
  if (status === "new") return "badge-primary";
  if (status === "contacted") return "badge-info";
  if (status === "scheduled") return "badge-warning";
  return "badge-secondary";
};

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function applySearch() {
  store.fetchDemoRequests({ page: 1 });
}

function clearFilters() {
  store.filters.status = "";
  store.filters.q = "";
  store.fetchDemoRequests({ page: 1 });
}

function openDetails(item) {
  selectedRequest.value = item;
  adminNotes.value = item.admin_notes || "";
}

function closeDetails() {
  selectedRequest.value = null;
  adminNotes.value = "";
}

async function saveDetails() {
  if (!selectedRequest.value) return;

  await store.updateStatus(
    selectedRequest.value.id,
    selectedRequest.value.status,
    adminNotes.value
  );
  closeDetails();
}

async function quickUpdateStatus(item, status) {
  await store.updateStatus(item.id, status, item.admin_notes || null);
}

async function deleteRequest(item) {
  if (!window.confirm("Delete this demo request?")) return;
  await store.remove(item.id);
  if (selectedRequest.value?.id === item.id) {
    closeDetails();
  }
}

onMounted(() => {
  store.fetchDemoRequests({ page: 1 });
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper dq-platform">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h5 class="mb-0">Incoming Demo Requests</h5>
          <div class="d-flex align-items-center flex-wrap gap-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white"
                data-bs-toggle="dropdown"
              >
                {{ statusLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-2">
                <li>
                  <a href="javascript:void(0);" class="dropdown-item" @click="store.filters.status = ''; applySearch()">All Statuses</a>
                </li>
                <li>
                  <a href="javascript:void(0);" class="dropdown-item" @click="store.filters.status = 'new'; applySearch()">New</a>
                </li>
                <li>
                  <a href="javascript:void(0);" class="dropdown-item" @click="store.filters.status = 'contacted'; applySearch()">Contacted</a>
                </li>
                <li>
                  <a href="javascript:void(0);" class="dropdown-item" @click="store.filters.status = 'scheduled'; applySearch()">Scheduled</a>
                </li>
                <li>
                  <a href="javascript:void(0);" class="dropdown-item" @click="store.filters.status = 'closed'; applySearch()">Closed</a>
                </li>
              </ul>
            </div>
            <input
              v-model="store.filters.q"
              type="text"
              class="form-control"
              placeholder="Search name, company, email"
              @keyup.enter="applySearch"
            />
            <button class="btn btn-primary" @click="applySearch">Search</button>
            <button class="btn btn-light" @click="clearFilters">Clear</button>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Preferred Slot</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="store.loading">
                  <td colspan="7" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="!store.items.length">
                  <td colspan="7" class="text-center py-4 text-muted">No demo requests found.</td>
                </tr>
                <tr
                  v-for="item in store.items"
                  :key="item.id"
                  class="dq-cmd-pipeline-row"
                  :class="`is-${item.status}`"
                >
                  <td>{{ item.full_name }}</td>
                  <td>{{ item.company_name }}</td>
                  <td>{{ item.email }}</td>
                  <td>{{ item.preferred_date }} {{ item.preferred_time }}</td>
                  <td>
                    <span :class="['badge', statusBadgeClass(item.status)]">
                      {{ item.status }}
                    </span>
                  </td>
                  <td>{{ item.created_at?.slice(0, 10) }}</td>
                  <td class="text-end">
                    <div class="d-inline-flex gap-2">
                      <button class="btn btn-sm btn-light" @click="openDetails(item)">View</button>
                      <button
                        v-if="item.status === 'new'"
                        class="btn btn-sm btn-info"
                        @click="quickUpdateStatus(item, 'contacted')"
                      >
                        Mark Contacted
                      </button>
                      <button class="btn btn-sm btn-danger" @click="deleteRequest(item)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
    </div>
  </div>

  <div
    v-if="selectedRequest"
    class="modal fade show d-block"
    tabindex="-1"
    style="background: rgba(0, 0, 0, 0.45)"
    @click.self="closeDetails"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Demo Request Details</h5>
          <button type="button" class="btn-close" @click="closeDetails"></button>
        </div>
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-md-6"><strong>Name:</strong> {{ selectedRequest.full_name }}</div>
            <div class="col-md-6"><strong>Company:</strong> {{ selectedRequest.company_name }}</div>
            <div class="col-md-6"><strong>Email:</strong> {{ selectedRequest.email }}</div>
            <div class="col-md-6"><strong>Phone:</strong> {{ selectedRequest.phone || "—" }}</div>
            <div class="col-md-6"><strong>Company Size:</strong> {{ selectedRequest.company_size }}</div>
            <div class="col-md-6"><strong>Preferred Contact:</strong> {{ selectedRequest.preferred_contact }}</div>
            <div class="col-md-6"><strong>Preferred Date:</strong> {{ selectedRequest.preferred_date }}</div>
            <div class="col-md-6"><strong>Preferred Time:</strong> {{ selectedRequest.preferred_time }}</div>
            <div class="col-md-12"><strong>Timezone:</strong> {{ selectedRequest.timezone }}</div>
            <div class="col-md-12"><strong>Modules:</strong> {{ (selectedRequest.modules || []).join(", ") || "—" }}</div>
            <div class="col-md-12"><strong>Notes:</strong> {{ selectedRequest.notes || "—" }}</div>
            <div class="col-md-6">
              <label class="form-label">Status</label>
              <select v-model="selectedRequest.status" class="form-select">
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="scheduled">Scheduled</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div class="col-md-12">
              <label class="form-label">Admin Notes</label>
              <textarea v-model="adminNotes" class="form-control" rows="3"></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-light" @click="closeDetails">Cancel</button>
          <button class="btn btn-primary" @click="saveDetails">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>
